import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, merge } from 'rxjs';

import { BreadcrumbService } from '../../services/breadcrumb.service';

interface CrumbConfig {
    label?: string;
    key?: string;
    fallback?: string;
    url: string;
}

interface Breadcrumb {
    label: string;
    url: string;
}

@Component({
    selector: 'app-breadcrumb',
    template: `
        <nav class="breadcrumb" aria-label="Breadcrumb">
          @for (crumb of breadcrumbs; track crumb; let last = $last) {
            <span class="breadcrumb_item">
              @if (!last) {
                <a
                  class="breadcrumb_link"
                  [routerLink]="'/' + crumb.url"
                  [title]="crumb.label">
                  {{ crumb.label }}
                </a>
              }
              @if (last) {
                <span class="breadcrumb_current" [title]="crumb.label">{{ crumb.label }}</span>
              }
              @if (!last) {
                <span class="breadcrumb_sep">/</span>
              }
            </span>
          }
        </nav>
        `,
    styles: [`
        :host {
            display: block;
            min-width: 0;
        }
        .breadcrumb {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            flex-wrap: nowrap;
            gap: 8px;
            min-width: 0;
            color: inherit;
            font-size: 14px;
            line-height: 1.2;
        }
        .breadcrumb_item {
            display: inline-flex;
            align-items: center;
            min-width: 0;
            gap: 8px;
        }
        .breadcrumb_link,
        .breadcrumb_current {
            max-width: 220px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        .breadcrumb_link {
            color: inherit;
            opacity: 0.85;
            text-decoration: none;
        }
        .breadcrumb_link:hover {
            opacity: 1;
            text-decoration: underline;
        }
        .breadcrumb_current {
            color: inherit;
            font-weight: 600;
        }
        .breadcrumb_sep {
            color: inherit;
            opacity: 0.55;
            user-select: none;
        }
    `],
    imports: [RouterLink]
})
export class BreadcrumbComponent {
    breadcrumbs: Breadcrumb[] = [];

    private router = inject(Router);
    private breadcrumbService = inject(BreadcrumbService);
    private destroyRef = inject(DestroyRef);

    constructor() {
        const navigation$ = this.router.events.pipe(filter(event => event instanceof NavigationEnd));
        merge(navigation$, this.breadcrumbService.changes)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.breadcrumbs = this.buildBreadcrumbs(this.router.routerState.root);
            });
    }

    private buildBreadcrumbs(route: ActivatedRoute): Breadcrumb[] {
        const children = this.collectChildren(route);
        if (children.length === 0) {
            return [];
        }

        const leaf = children[children.length - 1];
        const config = leaf.snapshot.data['breadcrumb'] as CrumbConfig[] | undefined;
        if (!config) {
            return [];
        }

        const params = leaf.snapshot.params;
        return config.map(crumb => ({
            label: this.resolveLabel(crumb, params),
            url: this.interpolate(crumb.url, params),
        }));
    }

    private resolveLabel(crumb: CrumbConfig, params: Record<string, string>): string {
        if (crumb.key) {
            const scopeParam = this.getScopeParamKey(crumb.key);
            const scopedId = scopeParam ? params[scopeParam] : undefined;
            const fromService = scopedId
                ? this.breadcrumbService.getScopedLabel(crumb.key, scopedId)
                : this.breadcrumbService.getLabel(crumb.key);
            if (fromService) {
                return fromService;
            }
            return crumb.fallback ? this.interpolate(crumb.fallback, params) : crumb.key;
        }
        return crumb.label ?? '';
    }

    private getScopeParamKey(crumbKey: string): string | undefined {
        if (crumbKey === 'installation') {
            return 'idInstallation';
        }
        if (crumbKey === 'site') {
            return 'idSito';
        }
        if (crumbKey === 'silo') {
            return 'siloId';
        }
        return undefined;
    }

    private collectChildren(route: ActivatedRoute): ActivatedRoute[] {
        const result: ActivatedRoute[] = [];
        let current = route;
        while (current.firstChild) {
            current = current.firstChild;
            result.push(current);
        }
        return result;
    }

    private interpolate(template: string, params: Record<string, string>): string {
        return template.replace(/:(\w+)/g, (_, key) => params[key] ?? key);
    }
}
